/**
 * Redux slice responsible for managing office-related state including computers and office object.
 * @module officeSlice
 */

/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';

// Initial state of the office slice
const initialState = {
    officeObj: {},
    computers: []
};

/**
 * Slice containing reducers for managing office-related state.
 */
export const officeSlice = createSlice({
    name: 'office',
    initialState,
    reducers: {
        /**
         * Action creator to set the list of computers.
         * @param {Object} state - Current state of the office slice.
         * @param {Array} action - Payload containing the list of computers.
         */
        setComputers: (state, action) => {
            const computers = action.payload.map((c) => ({
                ...c,
                _id: c._id?.toString(),
                credentials: Array.isArray(c.credentials)
                    ? c.credentials.map((cred) => ({
                          ...cred,
                          _id: cred._id?.toString(),
                          computerId: cred.computerId?.toString()
                      }))
                    : []
            }));

            state.computers = computers.sort((x) =>
                x.retired === true ? 0 : -1
            );
        },
        /**
         * Action creator to set the office object.
         * @param {Object} state - Current state of the office slice.
         * @param {Object} action - Payload containing the office object.
         */
        setOfficeObj: (state, action) => {
            state.officeObj = action.payload;
        },
        addComputer: (state, action) => {
            state.computers.push({
                ...action.payload,
                _id: action.payload._id?.toString(),
                credentials: []
            });
        },
        removeComputer: (state, action) => {
            state.computers = state.computers.filter(
                (computer) => computer._id !== action.payload
            );
        },
        addCredential: (state, action) => {
            if (!action.payload?._id) {
                return;
            }

            const payload = {
                ...action.payload,
                _id: action.payload._id?.toString(),
                computerId: action.payload.computerId?.toString()
            };

            const computerIndex = state.computers.findIndex(
                (c) => c._id === payload.computerId
            );
            if (computerIndex === -1) return;

            state.computers[computerIndex].credentials.push(payload);
        },
        removeCredential: (state, action) => {
            const { computerId, credentialId } = action.payload;

            const computerIndex = state.computers.findIndex(
                (c) => c._id === computerId
            );
            if (computerIndex === -1) return;

            state.computers[computerIndex].credentials = state.computers[
                computerIndex
            ].credentials.filter((cred) => cred._id !== credentialId);
        },
        updateComputerRetiredState: (state, action) => {
            const computerIndex = state.computers.findIndex(
                (c) => c._id === action.payload.computerId
            );
            if (computerIndex === -1) return;

            Object.assign(state.computers[computerIndex], {
                retired: action.payload.retiredState,
                dateRetired: action.payload.dateRetired,
                retiredBy: action.payload.retiredBy
            });
        },
        updateComputer: (state, action) => {
            const computerIndex = state.computers.findIndex(
                (c) => c._id === action.payload._id
            );
            if (computerIndex === -1) return;

            state.computers[computerIndex] = {
                ...action.payload,
                _id: action.payload._id?.toString()
            };
        },
        updateCredential: (state, action) => {
            const payload = {
                ...action.payload,
                _id: action.payload._id?.toString(),
                computerId: action.payload.computerId?.toString()
            };

            const updated = state.computers.some((computer) => {
                const credIndex = computer.credentials.findIndex(
                    (cred) => cred._id === payload._id
                );

                if (credIndex === -1) return false;

                // ✅ MERGE update (preserve existing fields)
                computer.credentials[credIndex] = {
                    ...computer.credentials[credIndex],
                    ...payload
                };

                // 🔍 TEMP DEBUG LOG (remove later)
                console.log(
                    'UPDATED CREDENTIAL IN REDUX:',
                    computer.credentials[credIndex]
                );

                return true; // stops `.some()`
            });

            if (!updated) {
                console.warn(
                    'updateCredential: credential not found in Redux',
                    payload
                );
            }
        },
        updateOfficeSecureProtocol: (state, action) => {
            state.officeObj.inSecureProtocol = action.payload;
        },
        updateOfficeActiveState: (state, action) => {
            state.officeObj.active = action.payload;
        },
        updateOfficeSysName: (state, action) => {
            state.officeObj.officeSysName = action.payload;
        }
    }
});

/**
 * Selector function to retrieve the list of computers from the state.
 * @param {Object} state - Redux state containing the office slice.
 * @returns {Array} The list of computers.
 */
export const selectComputers = (state) => state.office.computers;

/**
 * Selector function to retrieve the office object from the state.
 * @param {Object} state - Redux state containing the office slice.
 * @returns {Object} The office object.
 */
export const selectOfficeObj = (state) => state.office.officeObj;

export const {
    setComputers,
    addComputer,
    removeComputer,
    addCredential,
    removeCredential,
    updateComputerRetiredState,
    updateComputer,
    updateCredential,
    setOfficeObj,
    updateOfficeSecureProtocol,
    updateOfficeActiveState,
    updateOfficeSysName
} = officeSlice.actions;

const officeSliceReducer = officeSlice.reducer;

export default officeSliceReducer;
